import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import classNames from 'classnames';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useWindowSize from '../../hooks/useWindowSize';
import * as AppPropTypes from '../../lib/PropTypes';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';

import styles from '../../../styles/partials/sport-banner.module.scss';

const propTypes = {
    gameStatsComponent: PropTypes.elementType.isRequired,
    gameStatsDetailedComponent: PropTypes.elementType.isRequired,
    showBackLink: PropTypes.bool,
    backHref: PropTypes.string,
    sportGame: AppPropTypes.sportGame,
    backLabel: AppPropTypes.message,
    label: AppPropTypes.message,
    onBackClick: PropTypes.func,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
    showBackLink: false,
    backHref: null,
    sportGame: null,
    backLabel: null,
    label: null,
    onBackClick: null,
};

function SportBanner({
    gameStatsComponent: GameStatsComponent,
    gameStatsDetailedComponent: GameStatsDetailedComponent,
    showBackLink,
    backHref,
    sportGame,
    backLabel,
    label,
    onBackClick,
    className,
}) {
    const [showDetails, setShowDetails] = useState(false);
    const { backgroundColor = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const finalBgColor = isString(backgroundColor) ? [backgroundColor] : backgroundColor;
    const frameBg = useMemo(
        () =>
            finalBgColor !== null
                ? `${
                      finalBgColor.length === 1
                          ? finalBgColor[0]
                          : `${finalBgColor[0]} linear-gradient(${finalBgColor.join(',')})`
                  }`
                : null,
        [finalBgColor],
    );

    const { height: windowHeight = 0 } = useWindowSize();
    const scrollableEl = useRef(null);
    const [frameHeight, setFrameHeight] = useState(0);
    const onGeneralStatsClick = useCallback(() => {
        const { top = 0 } = scrollableEl.current.getBoundingClientRect();
        setFrameHeight(windowHeight - top);

        scrollableEl.current.scrollTo({ top: 0 });
        disableBodyScroll(scrollableEl.current);
        setShowDetails(true);
    }, [windowHeight]);

    const onDetailedBackClick = useCallback(() => {
        enableBodyScroll(scrollableEl.current);
        setShowDetails(false);
    }, []);

    useEffect(() => {
        const { top = 0 } = scrollableEl.current.getBoundingClientRect();
        setFrameHeight(windowHeight - top);
    }, [windowHeight]);

    useEffect(
        () => () => {
            clearAllBodyScrollLocks();
        },
        [],
    );

    const gridIcon = useMemo(() => [...Array(6)], []);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.showBackLink]: showBackLink,
                    [styles.showDetails]: showDetails,
                },
                themeClassName,
                className,
            ])}
        >
            <div className={styles.backToAllQuestionsContainer}>
                <Button className={styles.backToAllQuestions} onClick={onBackClick} href={backHref}>
                    <span className={styles.icon}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </span>
                    <span className={styles.label}>{backLabel}</span>
                </Button>
                <div className={styles.labelContainer}>
                    <span className={styles.icon}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </span>
                    <span className={styles.label}>{label}</span>
                </div>
            </div>
            <Button onClick={onGeneralStatsClick} className={styles.generalStatsContainer}>
                <GameStatsComponent className={styles.generalStats} sportGame={sportGame} />
                <span className={styles.arrow}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </span>
            </Button>
            <div className={styles.detailedStatsContainer}>
                <div className={styles.detailedStatsTopBanner}>
                    <Button
                        className={styles.detailedStatsBackButton}
                        onClick={onDetailedBackClick}
                    >
                        <span className={styles.icon}>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </span>
                        <span className={styles.label}>
                            <FormattedMessage defaultMessage="Back" description="Button label" />
                        </span>
                    </Button>
                    <div className={styles.boxScore}>
                        <div className={styles.gridIcon}>
                            {gridIcon.map((_, squareIndex) => (
                                <span key={`square-${squareIndex + 1}`} />
                            ))}
                        </div>
                        <div className={styles.label}>
                            <FormattedMessage defaultMessage="Boxscore" description="Tab label" />
                        </div>
                    </div>
                </div>
                <div
                    className={styles.detailedStatsFrame}
                    style={{ height: frameHeight, background: frameBg }}
                    ref={scrollableEl}
                >
                    <GameStatsDetailedComponent
                        className={styles.detailedStats}
                        sportGame={sportGame}
                    />
                </div>
            </div>
        </div>
    );
}

SportBanner.propTypes = propTypes;
SportBanner.defaultProps = defaultProps;

export default SportBanner;
